import { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { PageHeadingContext } from '@osu/react-ui'
import { AppContext } from "../../App/context";
import { authenticationFlowPaths } from "../../Routes/paths";

/**
 * Extracts the original redirect path from the OAuth state query parameter.
 * Amplify encodes the customState as a hex suffix: "<random>-<hex-encoded-path>".
 * This is necessary because the customOAuthState Hub event fires before React
 * mounts its listener — reading the path directly from the URL is the only
 * reliable way to recover it.
 */
function extractRedirectPathFromState(search) {
  try {
    const params = new URLSearchParams(search);
    const stateParam = params.get('state');
    if (!stateParam) return null;

    const dashIndex = stateParam.lastIndexOf('-');
    if (dashIndex === -1) return null;

    const hex = stateParam.slice(dashIndex + 1);
    if (!/^[0-9a-f]+$/i.test(hex)) return null;

    const decoded = decodeURIComponent(hex.replace(/../g, '%$&'));

    const exclusions = authenticationFlowPaths.map((p) => p.path);
    if (decoded.startsWith('/') && !exclusions.includes(decoded.split('?')[0])) {
      return decoded;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function LogIn(props = {}) {
  const { redirectPath } = props;
  const { setHeading } = useContext(PageHeadingContext)
  const { handleSignIn } = useContext(AppContext)
  const authStatus = useSelector((state) => state?.authentication?.status);
  const location = useLocation()
  const [error, setError] = useState(false)

  const signInAttempted = useRef(false);

  const resolvedRedirectPath =
    location?.state?.redirectPath ||
    extractRedirectPathFromState(location?.search) ||
    redirectPath;

  useEffect(() => {
    if (signInAttempted.current || error || ['loading', 'success'].includes(authStatus)) {
      return;
    }

    signInAttempted.current = true;

    const signIn = async () => {
      try {
        await handleSignIn(resolvedRedirectPath);
      } catch (err) {
        console.error("error", err);
        setError(true);
      }
    };

    signIn();
  }, [authStatus, error, handleSignIn, resolvedRedirectPath]);
  
  useEffect(() => {
    setHeading && setHeading('Log In')
  }, [setHeading])

  if (authStatus === "success") {
    return <Navigate to={resolvedRedirectPath || '/'} />;
  } else if (authStatus === "loading") {
    return <div>Logging in</div>;
  }

  return (
    <div>
      There was an error logging you in.
      <button
        className="mt-2"
        variant="contained"
        color="primary"
        aria-label={`Retry logging in}`}
        onClick={() => handleSignIn(redirectPath)}
      >
        Log In
      </button>
    </div>
  );
}

export default LogIn;
